import React, { useState } from 'react'
import { cn } from '@utils/safeTs'
import styles from './EditorBottomBar.module.sass'
import useTrans from '@hooks/useTrans'
import { useTutorialContext } from 'context/TutorialContext'
import { Clipboard, Close, Copy, Info, More, Tutorial } from '@components/icons'
import { ButtonIcon, FormToggle, Tooltip } from '@components/atoms'
import { EDITOR_VIDEO_GUIDE_URL } from '@utils/editor-bozze'

interface Props {
  showPreview?: boolean
  setShowPreview?: (v: boolean) => void
  hasCopiedStyle?: boolean
  syncAllCanvases?: boolean
  syncAllCanvasesDisabled?: boolean
  onCopyStyle?: () => void
  onPasteStyle?: () => void
  onSyncAllCanvasesChange?: (checked: boolean) => void
  className?: string
}

const EditorBottomBar = (props: Props) => {
  const {
    showPreview = false,
    hasCopiedStyle = false,
    syncAllCanvases = false,
    syncAllCanvasesDisabled = false,
    onCopyStyle,
    onPasteStyle,
    onSyncAllCanvasesChange,
    className = '',
  } = props

  const t = useTrans()
  const [open, setOpen] = useState(true)
  const [tutorialOpen, setTutorialOpen] = useState(true)
  const { clearTutorial } = useTutorialContext()

  return (
    <div
      className={cn(
        styles.root,
        open ? styles[`root--open`] : '',
        showPreview ? styles[`root--preview`] : '',
        className
      )}
    >
      <div className={styles.wrap}>
        {!showPreview && (
          <div className={styles.actions}>
            <Tooltip id="editorbottombar__tutorial" label={t('Mostra tutorial')} pos="top">
              <ButtonIcon
                ariaLabel={t('Visualizza tutorial')}
                icon={<Tutorial />}
                variant="ghost"
                size="lg"
                onClick={() => {
                  clearTutorial('editor')
                  setTutorialOpen(true)
                }}
              />
            </Tooltip>
            <Tooltip id={'editorbottombar_copy'} label={t('Copia stile')} pos={'top'}>
              <ButtonIcon
                ariaLabel={t('Copia stile')}
                icon={<Copy />}
                size="lg"
                variant="ghost"
                onClick={onCopyStyle}
              />
            </Tooltip>
            <Tooltip id={'editorbottombar_paste'} label={t('Incolla stile')} pos={'top'}>
              <ButtonIcon
                ariaLabel={t('Incolla stile')}
                icon={<Clipboard />}
                variant="ghost"
                size="lg"
                disabled={!hasCopiedStyle}
                onClick={onPasteStyle}
              />
            </Tooltip>
            <div className={styles.more}>
              <div className={styles.stileOptions}>
                <div className={styles.separator} />
                <FormToggle
                  id={`editorestileoptions_applyall`}
                  label={t('Applica stile a tutte le posizioni')}
                  tooltip={
                    syncAllCanvasesDisabled
                      ? t(
                          'Il sync è disponibile solo se la posizione corrente contiene almeno un elemento.'
                        )
                      : t(
                          'Ogni modifica fatta sulla posizione corrente viene replicata automaticamente su tutte le altre.'
                        )
                  }
                  checked={syncAllCanvases}
                  disabled={syncAllCanvasesDisabled}
                  onChange={(e) => onSyncAllCanvasesChange && onSyncAllCanvasesChange(e.target.checked)}
                  className={styles.apply}
                />
                <div className={styles.separator} />
              </div>
            </div>
            <Tooltip
              id="editorbottombar__more"
              label={t(open ? 'Nascondi opzioni' : 'Visualizza più opzioni')}
              pos="top"
            >
              <ButtonIcon
                icon={open ? <Close /> : <More />}
                size="lg"
                variant="ghost"
                ariaLabel={t(open ? 'Nascondi opzioni' : 'Visualizza più opzioni')}
                onClick={() => setOpen((prev) => !prev)}
              />
            </Tooltip>
          </div>
        )}
        {tutorialOpen && !showPreview && (
          <div className={styles.tutorialBox}>
            <div className={styles.tutorialIcon}>
              <Info />
            </div>
            <div className={styles.tutorialContent}>
              <p className={styles.tutorialText}>
                {t.rich('Guarda la {0} che abbiamo preparato per te', [
                  <a
                    href={EDITOR_VIDEO_GUIDE_URL}
                    target="_blank"
                    rel="noreferrer"
                    className={styles.tutorialLink}
                  >
                    {t('video guida')}
                  </a>,
                ])}
              </p>
            </div>
            <button
              type="button"
              className={styles.tutorialClose}
              aria-label={t('Chiudi tutorial')}
              onClick={() => setTutorialOpen(false)}
            >
              <Close />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default EditorBottomBar
